import * as React from 'react';
import { BasicConfigurationModel } from '@/modules/settings/types/basic';
import { basicConfigurationSchema } from '@/modules/settings/schema/BasicSettingsSchema';

type UpdateTuple<K extends keyof BasicConfigurationModel> = readonly [K, BasicConfigurationModel[K]];
type UpdateOptions = { stripUndefined?: boolean };

const validate = (data: BasicConfigurationModel) => {
  const validated = basicConfigurationSchema.safeParse(data);
  if (!validated.success) {
    return { valid: false, data: null, errors: validated.error.flatten().fieldErrors };
  }
  return { valid: true, data: validated.data, errors: {} };
};

/**
 * Manages local draft of Basic Configuration data.
 * Keeps unsaved edits in memory before sending to API.
 */
export function useDraftBaseSettings(initialData: Partial<BasicConfigurationModel> = {}) {
  const [draft, setDraft] = React.useState<Partial<BasicConfigurationModel>>(initialData || null);
  const [errors, setErrors] = React.useState<
    Partial<Record<keyof BasicConfigurationModel, string>>
  >({});
  const changedRef = React.useRef(false);

  React.useEffect(() => {
    setDraft(initialData || null);
  }, [initialData]);

  React.useEffect(() => {
    const run = async () => {
      // const result = await validate(draft as BasicConfigurationModel);
      // console.log("VALIDATE", { result, draft });
      // changedRef.current = false;
      // if (!result.valid) {
      //   setErrors(result.errors);
      // } else {
      //   setErrors({});
      // }
    };
    // run();
  }, [draft]);

  const updateFields = React.useCallback(
    (
      updates:
        | Partial<BasicConfigurationModel>
        | ReadonlyArray<UpdateTuple<keyof BasicConfigurationModel>>,
      options?: UpdateOptions
    ) => {
      // mark draft as changed
      changedRef.current = true;

      setDraft((prev) => {
        const base = prev ?? {};

        // narrow union explicitly instead of using a ternary that confuses TS
        let patchObj: Partial<BasicConfigurationModel>;
        if (Array.isArray(updates)) {
          patchObj = (updates as ReadonlyArray<
            UpdateTuple<keyof BasicConfigurationModel>
          >).reduce<Partial<BasicConfigurationModel>>((acc, [k, v]) => {
            // k is correlated with v via the tuple; assignment is safe
            (acc as any)[k] = v;
            return acc;
          }, {});
        } else {
          patchObj = updates as Partial<BasicConfigurationModel>;
        }

        // optionally drop keys with `undefined` values
        const normalized = options?.stripUndefined
          ? (Object.fromEntries(
            Object.entries(patchObj).filter(([, v]) => v !== undefined)
          ) as Partial<BasicConfigurationModel>)
          : patchObj;

        // merge and return next draft
        return { ...base, ...normalized };
      });
    },
    []
  ) as {
    // overloads exposed to callers (nice inference for tuples)
    (updates: Partial<BasicConfigurationModel>, options?: UpdateOptions): void;
    <K extends keyof BasicConfigurationModel>(
      updates: ReadonlyArray<UpdateTuple<K>>,
      options?: UpdateOptions
    ): void;
  };

  const updateField = React.useCallback(
    <K extends keyof BasicConfigurationModel>(field: K, value: BasicConfigurationModel[K]) => {
      changedRef.current = true;
      setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    []
  );

  const reset = React.useCallback(() => {
    setDraft(initialData || null);
  }, [initialData]);

  return {
    errors,
    isValid: !changedRef.current && Object.keys(errors).length === 0,
    data: draft,
    isDirty: Object.keys(draft).length,
    updateField,
    reset,
    updateFields,
  };
}
